import axios from "axios";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {createStore} from "vuex";
import VuexPersistence from "vuex-persist";
import {config} from "@/config/config";
import {getCleanVisibleLayers, getSSID, paramsToUrlQuery} from "@/js/utils";
import {get} from "idb-keyval";
import router from "@/js/router";
import localforage from 'localforage';
import {cloneDeep, debounce} from 'lodash';
import {DocumentCategoryEnum} from "@/js/enums/document-category";
import {db} from "@/js/store/offline/database";
import {prepareForSave, prepareForUse} from "@/js/store/offline/data";

let urlParams = typeof window !== "undefined" ? new URL(document.location).searchParams : new URLSearchParams();
let ssid = getSSID();

const localstoragePlugin = new VuexPersistence({
  key: 'vuex',
  storage: typeof window !== "undefined" ? window.localStorage : null,
  reducer: (state) => {
    let obj = cloneDeep(state);
    delete obj.endpointsInfo;
    obj[ssid] = {
      user: obj[ssid].user,
      token: obj[ssid].token,
      iframe: obj[ssid].iframe,
      drawingTools: {
        ...obj[ssid].drawingTools,
        finishEditFeature: false,
        featureUpdated: false,
        activeHorizontalButton: "",
        startExtent: false,
        startPointExtent: null,
        stopPointExtent: null,
      },
      mapSettings: obj[ssid].mapSettings || {
        centerIcon: "icon_map_center_default.svg",
        centerColor: "#000000",
      },
      mapState: {
        mode: undefined,
        drawMode: undefined,
        drawAction: undefined,
        selectedDate: undefined,
        ...obj[ssid].mapState,
      },
      selectedOrganizationId: obj[ssid].selectedOrganizationId,
      selectedEmployeeId: obj[ssid].selectedEmployeeId,
      projectionConversion: null,
      formTemplatesParams: obj[ssid].formTemplatesParams,
      appUISettings: obj[ssid].appUISettings || {
        drawerOpened: true,
        filtersExpanded: true,
        maximized: false,
        favorites: []
      },
      analytics: obj[ssid].analytics,
      analyticsId: obj[ssid].analyticsId || 'local'
    };
    return obj;
  }
})

const debouncedSaveState = debounce(async (key, state) => {
  try {
    await localforage.setItem(key, state);
  } catch (error) {
    console.error("Failed to save state", error);
  }
}, 500);

const localforagePlugin = new VuexPersistence({
  key: 'vuex',
  storage: localforage,
  asyncStorage: true,
  restoreState: async (key, storage) => {
    try {
      //await localforage.ready();
      const state = await localforage.getItem('vuex');
      return state || {};
    } catch (err) {
      console.error('Failed to restore state from IndexedDB', err);
      return {};
    }
  },
  saveState: async (key, state, storage) => {
    try {
      delete state.endpointsInfo;
      debouncedSaveState(key, state);
    } catch (err) {
      console.error('Failed to save state to IndexedDB', err);
    }
  },
  reducer: (state) => {
    let obj = cloneDeep(state);
    obj[ssid] = {
      common: obj[ssid].common,
      currentProject: {
        //...obj[ssid].currentProject,
        localProjects: obj[ssid]?.currentProject?.localProjects,
        currentLayer: obj[ssid]?.currentProject?.currentLayer,
        visibleLayers: obj[ssid]?.currentProject?.visibleLayers
          ? getCleanVisibleLayers(obj[ssid].currentProject.visibleLayers)
          : [],
        projects: null,
        search: null,
      },
      currentGlobalComponents: obj[ssid].currentGlobalComponents,
      currentStages: obj[ssid].currentStages,
      currentForms: obj[ssid].currentForms,
    };
    return obj;
  },
});

let http = axios.create({
  baseURL: config.API_URL,
});

export const setFingerprintHeader = async (instances) => {
  let fingerprint = localStorage.getItem('user-fingerprint');
  if (!fingerprint) {
    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const result = await fp.get();
    fingerprint = result.visitorId;
    localStorage.setItem('user-fingerprint', fingerprint);
  }
  instances.forEach(instance => {
    instance.defaults.headers.common["x-uuid"] = fingerprint;
  });
  axios.defaults.headers.common["x-uuid"] = fingerprint;
};

if(config.APP_ORGANIZATION_ID){
  http.defaults.headers.common["x-organization-id"] = config.APP_ORGANIZATION_ID;
}

let httpSync = axios.create({
  baseURL: config.SYNC_API_URL,
});

let httpLocal = axios.create({
  baseURL: config.LOCAL_API_URL_SIGNATURE,
});

let httpWmts = axios.create({
  baseURL: config.WMTS_API_URL,
});

let httpWms = axios.create({
  baseURL: config.WMS_API_URL,
});

let httpWfs = axios.create({
  baseURL: config.WFS_API_URL,
});

setFingerprintHeader([http, httpSync, httpLocal, httpWmts, httpWms, httpWfs]);

let currentLanguage = sessionStorage.getItem("lang") || 'ro';
http.defaults.headers.common["accept-language"] = currentLanguage;

window.addEventListener('langChanged', () => {
  let lang = sessionStorage.getItem("lang");
  if (lang !== currentLanguage) {
    currentLanguage = lang;
    http.defaults.headers.common["accept-language"] = currentLanguage;
  }
});

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (typeof error.response === "undefined" && error?.config?.url !== "utils/check") {
      setTimeout(() => {
        checkConnectivity(0)
      }, 1000)
    }
    return Promise.reject(error);
  }
);

[httpSync, httpLocal, httpWmts, httpWms, httpWfs].forEach(instance => {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (typeof error.response === "undefined" && error?.config?.url !== "utils/check") {
        setTimeout(() => {
          checkConnectivity(0)
        }, 1000)
      }
      return Promise.reject(error);
    }
  );
});

function checkConnectivity(noOfTries) {
  store.dispatch("checkServer").then(respone => {
  })
    .catch(error => {
      noOfTries++;
      if (noOfTries > 3)
        router.currentRoute.value.meta.layout = "ServerError";
      else checkConnectivity(noOfTries)
    })
}

async function clearSession(){
  try {
    await localforage.clear();
    if (typeof localStorage !== "undefined") localStorage.clear();
    if (typeof sessionStorage !== "undefined") sessionStorage.clear();
    try { delete document.cookie; } catch (e) {}
    if (typeof window !== "undefined" && window.indexedDB && window.indexedDB.databases) {
      window.indexedDB.databases().then((dbs) => {
        for (let i = 0; i < dbs.length; i++) if (dbs[i]?.name) window.indexedDB.deleteDatabase(dbs[i].name);
      })
    }
  } catch (e) {}
}

const throwError = (error, dispatch, resolve, reject) => {
  if (/428|498/.test(error?.response?.status)) {
    reject(error);
  } else if (/401|403/.test(error?.response?.status)) {
    dispatch("signOut")
      .then((res) => {
        router.push("/login")
      })
      .catch((error) => {
        router.push("/login")
      });
      console.log(error.response?.data?.details?.message ||
      error.response?.data?.details ||
      error.response?.data?.message ||
      error?.response?.data ||
      error)
    /*reject(
      error.response?.data?.details?.message ||
      error.response?.data?.details ||
      error.response?.data?.message ||
      error?.response?.data ||
      error
    );*/
  } else
    reject(
      error.response?.data?.details?.message ||
      error.response?.data?.details ||
      error.response?.data?.message ||
      error?.response?.data ||
      error
    );
};

const store = createStore({
  state: {
    formEdit: {
      selectedFormId: null,
      passedProps: {}
    },
    currentFlows: [],
    endpointsInfo: null
  },
  mutations: {
    SET_FORM_EDIT(state, data) {
      state.formEdit = data;
    },
    SET_CURRENT_FLOWS(state, data) {
      state.currentFlows = data;
    },
    SET_ENDPOINTS_INFO(state, data) {
      state.endpointsInfo = data;
    },
    SET_COMMON_DATA(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].common = data;
      console.log(new Blob([JSON.stringify(state[ssid].common)]).size / 1024)
    },

    SET_CURRENT_PROJECT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (data && data.layers) {
        state[ssid].currentProjectLayers = data.layers;
        delete data.layers;
      }
      if (data && data.visibleLayers) {
        data.visibleLayers = getCleanVisibleLayers(data.visibleLayers);
      }
      state[ssid].currentProject = data;
    },
    SET_UPLOAD_WORKER(state, data) {
      console.log("SET_UPLOAD_WORKER", state, data, ssid);
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].uploadWorker = data;
    },
    SET_UPLOAD_FILE(state, data) {
      ssid = ssid ? ssid : data.ssid;
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].uploadFile = data;
    },
    SET_USER(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].user = data ? {
        ...state[ssid].user,
        ...data
      } : data;
    },
    SET_TOKEN(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].token = data;
    },
    SET_SELECTED_ORGANIZATION_ID(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].selectedOrganizationId = data;
    },
    SET_SELECTED_EMPLOYEE_ID(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].selectedEmployeeId = data;
    },
    SET_FORM_TEMPLATES_PARAMS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].formTemplatesParams = data;
    },
    SET_IFRAME(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].iframe = data;
    },

    //pure function for mapBoxDraw
    SET_MAP_STATE(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].mapState = data;
    },
    SET_MAP_SETTINGS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].mapSettings = data;
    },
    SET_APP_UI_SETTINGS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].appUISettings = data;
    },
    SET_ANALYTICS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].analytics = data;
    },
    SET_ANALYTICS_ID(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].analyticsId = data;
    },
    SET_FINISH_EDIT_FEATURE(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.finishEditFeature = data;
    },
    SET_FEATURE_UPDATED(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.featureUpdated = data;
    },
    SET_ACTIVE_HORIZONTAL_BUTTON(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.activeHorizontalButton = data;
    },
    SET_ACTIVE_CENTER_TOOLBOX_BUTTON(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.topCenterEditToolBarActiveButtons = data;
    },
    SET_START_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.startExtent = data;
    },
    SET_START_POINT_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.startPointExtent = data;
    },
    SET_STOP_POINT_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.stopPointExtent = data;
    },
    SET_PROJECTION_CONVERSION(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].projectionConversion = data;
    },
    SET_CURRENT_GLOBAL_COMPONENTS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentGlobalComponents = data;
    },
    SET_CURRENT_STAGES(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentStages = data;
    },
    SET_CURRENT_FORMS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentForms = data;
    }
  },
  getters: {
    GET_FORM_EDIT(state) {
      return state.formEdit;
    },
    GET_CURRENT_FLOWS(state) {
      return state.currentFlows;
    },
    GET_ENDPOINTS_INFO(state) {
      return state.endpointsInfo;
    },
    GET_COMMON_DATA(state) {
      return state[ssid] && state[ssid].common ? state[ssid].common : {};
    },
    GET_SSID(state) {
      return ssid;
    },
    GET_CURRENT_PROJECT(state) {
      return state[ssid] && state[ssid].currentProject
        ? {
          ...state[ssid].currentProject,
          layers: state[ssid].currentProjectLayers,
        }
        : null;
    },
    GET_USER(state) {
      return state[ssid] && state[ssid].user ? state[ssid].user : null;
    },
    GET_UPLOAD_WORKER(state) {
      return state[ssid] && state[ssid].uploadWorker ? state[ssid].uploadWorker : null;
    },
    GET_UPLOAD_FILE(state) {
      return state[ssid] && state[ssid].uploadFile ? state[ssid].uploadFile : null;
    },
    GET_TOKEN(state) {
      return state[ssid] && state[ssid].token ? state[ssid].token : null;
    },
    GET_SELECTED_ORGANIZATION_ID(state) {
      return state[ssid] && state[ssid].selectedOrganizationId ? state[ssid].selectedOrganizationId : null;
    },
    GET_SELECTED_EMPLOYEE_ID(state) {
      return state[ssid] && state[ssid].selectedEmployeeId ? state[ssid].selectedEmployeeId : null;
    },
    GET_FORM_TEMPLATES_PARAMS(state) {
      return state[ssid] && state[ssid].formTemplatesParams ? state[ssid].formTemplatesParams : null;
    },
    GET_IFRAME(state) {
      return state[ssid] && state[ssid].iframe ? state[ssid].iframe : null;
    },
    GET_MAP_BOX_DRAW(state) {
      return state[ssid] && state[ssid].drawingTools ? state[ssid].drawingTools : null;
    },
    GET_STATE(state) {
      return state[ssid];
    },

    //getter fro mapBoxDraw
    GET_MAP_STATE(state) {
      return state[ssid] && state[ssid]?.mapState;
    },
    GET_MAP_SETTINGS(state) {
      return (state[ssid] && state[ssid]?.mapSettings) || {
        centerIcon: "icon_map_center_default.svg",
        centerColor: "#000000",
      };
    },
    GET_APP_UI_SETTINGS(state) {
      return (state[ssid] && state[ssid]?.appUISettings) || {
        drawerOpened: true,
        filtersExpanded: true,
        maximized: false,
        favorites: []
      };
    },
    GET_ANALYTICS(state) {
      return (state[ssid] && state[ssid]?.analytics) || {};
    },
    GET_ANALYTICS_ID(state) {
      return (state[ssid] && state[ssid]?.analyticsId) || 'local';
    },
    GET_FINISH_EDIT_FEATURE(state) {
      return state[ssid] && state[ssid]?.drawingTools?.finishEditFeature
        ? state[ssid].drawingTools.finishEditFeature
        : false;
    },
    GET_FEATURE_UPDATED(state) {
      return state[ssid] && state[ssid]?.drawingTools?.featureUpdated ? state[ssid].drawingTools.featureUpdated : false;
    },
    GET_ACTIVE_HORIZONTAL_BUTTON(state) {
      return state[ssid] && state[ssid]?.drawingTools?.activeHorizontalButton
        ? state[ssid].drawingTools.activeHorizontalButton
        : null;
    },
    GET_START_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.startExtent ? state[ssid].drawingTools.startExtent : false;
    },
    GET_START_POINT_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.startPointExtent
        ? state[ssid].drawingTools.startPointExtent
        : null;
    },
    GET_STOP_POINT_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.stopPointExtent
        ? state[ssid].drawingTools.stopPointExtent
        : null;
    },
    GET_PROJECTION_CONVERSION(state) {
      return state[ssid] && state[ssid]?.projectionConversion ? state[ssid].projectionConversion : null;
    },
    GET_CURRENT_GLOBAL_COMPONENTS(state) {
      return state[ssid] && state[ssid]?.currentGlobalComponents ? state[ssid].currentGlobalComponents : [];
    },
    GET_CURRENT_STAGES(state) {
      return state[ssid] && state[ssid]?.currentStages ? state[ssid].currentStages : {};
    },
    GET_CURRENT_FORMS(state) {
      return state[ssid] && state[ssid]?.currentForms ? state[ssid].currentForms : {};
    }
  },
  actions: {
    setFormEdit({commit}, data) {
      commit('SET_FORM_EDIT', data);
    },
    setCurrentFlows({commit}, data) {
      commit('SET_CURRENT_FLOWS', data);
    },
    setEndpointsInfo({commit}, data) {
      commit('SET_ENDPOINTS_INFO', data);
    },
    setCommonData({commit}, data) {
      commit("SET_COMMON_DATA", data);
    },
    setMapSettings({commit}, data) {
      commit("SET_MAP_SETTINGS", data);
    },
    setAppUISettings({commit}, data) {
      commit("SET_APP_UI_SETTINGS", data);
    },
    setAnalytics({commit}, data) {
      commit("SET_ANALYTICS", data);
    },
    setAnalyticsId({commit}, data) {
      commit("SET_ANALYTICS_ID", data);
    },
    setIframe({commit}, data) {
      commit("SET_IFRAME", data);
    },
    setSelectedOrganizationId({commit}, data) {
      commit("SET_SELECTED_ORGANIZATION_ID", data);
    },
    setSelectedEmployeeId({commit}, data) {
      commit("SET_SELECTED_EMPLOYEE_ID", data);
    },
    setFormTemplatesParams({commit}, data) {
      commit("SET_FORM_TEMPLATES_PARAMS", data);
    },
    setCurrentProject({commit}, data) {
      commit("SET_CURRENT_PROJECT", data);
    },
    setCurrentGlobalComponents({commit}, data) {
      commit("SET_CURRENT_GLOBAL_COMPONENTS", data);
    },
    setCurrentStages({commit}, data) {
      commit("SET_CURRENT_STAGES", data);
    },
    setCurrentForms({commit}, data) {
      commit("SET_CURRENT_FORMS", data);
    },
    setUser({commit}, data) {
      commit("SET_USER", data);
    },
    setUploadWorker({commit}, data) {
      commit("SET_UPLOAD_WORKER", data);
    },
    setUploadFile({commit}, data) {
      commit("SET_UPLOAD_FILE", data);
    },
    signIn: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        data = {
          ...data,
          moduleTypeId: parseInt(config.APP_MODULE_TYPE)
        }

        /*localforage.clear();
        if (typeof window !== "undefined" && window.localStorage)
          window.localStorage.clear();
        sessionStorage.clear();*/

        http
          .post("users/login", data)
          .then((response) => {
            commit("SET_USER", response.data.user);
            commit("SET_TOKEN", response.data.token);
            http.defaults.headers.common["x-access-token"] = response.data.token;
            if (config.APP_TYPE === "default") {
              dispatch("getRoles")
                .then((res) => {
                })
                .catch((err) => {
                });
            }
            dispatch("getModules", {query: {moduleTypeId: parseInt(config.APP_MODULE_TYPE), reduceData: true}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getRightsList", {query: {reduceData: true}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getAllDocumentTypes", {query: {reduceData: true}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getDocumentCategories", {query: {reduceData: true}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getConfigs")
              .then((res) => {
              })
              .catch((err) => {
              });
            // get currentProject from IndexedDB if exists
            get("userId").then((usr) => {
              if (parseInt(usr) === response.data.user.userId) {
                get("currentProject")
                  .then((val) => {
                    dispatch("setCurrentProject", JSON.parse(val)).catch((err) => {
                    });
                  })
                  .catch((err) => {
                  });
              }
            });
            resolve(response.data);
          })
          .catch((error) => {
            resolve(error)
            throwError(error, dispatch, resolve, reject)
          });
      });
    },
    checkPhoneOtp: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .post("users/phone/check-otp", data)
          .then((response) => {
            dispatch("getUser", {
              query: {
                userId: data?.userId,
              },
            })
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
              });
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    checkEmailOtp: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .post("users/email/check-otp", data)
          .then((response) => {
            dispatch("getUser", {
              query: {
                userId: data?.userId,
              },
            })
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
              });
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    iframeSignIn: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        commit("SET_USER", data.user);
        commit("SET_TOKEN", data.token);
        http.defaults.headers.common["x-access-token"] = data.token;
        if (config.APP_TYPE === "default") {
          dispatch("getRoles")
            .then((res) => {
            })
            .catch((err) => {
            });
        }
        dispatch("getModules", {query: {moduleTypeId: parseInt(config.APP_MODULE_TYPE), reduceData: true}})
          .then((res) => {
          })
          .catch((err) => {
          });
        dispatch("getRightsList", {query: {reduceData: true}})
          .then((res) => {
          })
          .catch((err) => {
          });
        dispatch("getAllDocumentTypes", {query: {reduceData: true}})
          .then((res) => {
          })
          .catch((err) => {
          });
        dispatch("getDocumentCategories", {query: {reduceData: true}})
          .then((res) => {
          })
          .catch((err) => {
          });
        dispatch("getConfigs")
          .then((res) => {
          })
          .catch((err) => {
          });
        if (data?.user?.userId) {
          dispatch("getUserOrganization", {organizationCategoryId: 1})
            .then((res) => {
            })
            .catch((err) => {
            });
        }
        // get currentProject from IndexedDB if exists
        get("userId").then((usr) => {
          if (parseInt(usr) === data.user.userId) {
            get("currentProject")
              .then((val) => {
                dispatch("setCurrentProject", JSON.parse(val)).catch((err) => {
                });
              })
              .catch((err) => {
              });
          }
        });
        resolve(data);
      });
    },
    signOut: ({commit, state, dispatch}, data) => {
      return new Promise((resolve, reject) => {
        ssid = ssid ? ssid : data.ssid;
        http.defaults.headers.common["x-access-token"] =
          urlParams.get("api_key") || (ssid ? state[ssid].token : undefined) || data?.token;

        commit("SET_COMMON_DATA", {
          ...state[ssid].common,
          lastUrl: /login|embed/.test(window.location.pathname) ? '' : window.location.pathname,
        });

        if (!http.defaults.headers.common["x-access-token"]) {
          resolve();
          return;
        }

        http
          .post("users/logout")
          .then(async (response) => {
            commit("SET_USER", null);
            commit("SET_TOKEN", null);
            commit("SET_CURRENT_PROJECT", null);
            commit("SET_IFRAME", null);
            commit("SET_SELECTED_ORGANIZATION_ID", null);
            commit("SET_SELECTED_EMPLOYEE_ID", null);
            http.defaults.headers.common["x-access-token"] = "";

            await clearSession();

            resolve(response.data);
          })
          .catch(async (error) => {
            commit("SET_USER", null);
            commit("SET_TOKEN", null);
            commit("SET_CURRENT_PROJECT", null);
            commit("SET_IFRAME", null);
            commit("SET_SELECTED_ORGANIZATION_ID", null);
            commit("SET_SELECTED_EMPLOYEE_ID", null);
            http.defaults.headers.common["x-access-token"] = "";

            await clearSession();

            reject(
              error.response?.data?.details?.message ||
              error.response?.data?.details ||
              error.response?.data?.message ||
              error?.response?.data ||
              error
            );
          });
      });
    },
    getEmailOtp: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        if (urlParams.get("api_key") || state[ssid]?.token) {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid]?.token;
        }
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/email/otp" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSmsOtp: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        if (urlParams.get("api_key") || state[ssid]?.token) {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid]?.token;
        }
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/phone/otp" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCountries: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("countries/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addCountry: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("countries/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editCountry: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("countries/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteCountry: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("countries" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editCounty: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("counties" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCounties: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("counties/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addCounty: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("counties" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteCounty: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("counties/delete" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCities: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("cities/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addCity: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("cities" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editCity: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("cities" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteCity: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("cities" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    processGeoreference: ({state, dispatch}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/georeference/process", data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addGeoreferenceTask: ({state, dispatch}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents", data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTaskCategories: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("task-categories/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addTaskCategory: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("task-categories/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editTaskCategory: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("task-categories/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteTaskCategory: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("task-categories/delete" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getTaskTypes: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("task-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addTaskType: ({state, dispatch, commit}, data) => {
      console.log(data);
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("task-types/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateTaskType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("task-types/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteTaskType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("task-types/delete" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getStatuses: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid]?.token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("statuses/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getStatus: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid]?.token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("statuses/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addStatus: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("statuses" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editStatus: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("statuses" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteStatus: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("statuses" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getUserTypes: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("user-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addUserType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("user-types/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editUserType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("user-types/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteUserTypes: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("user-types/delete" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTokenTypes: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("token-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addTokenType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("token-types/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editTokenType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("token-types/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteTokenTypes: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("token-types/delete" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("document-categories/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentCategories: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("document-categories/get-all" + paramsToUrlQuery(data?.query))
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, documentCategories: response.data?.map(x => ({documentCategoryId: x.documentCategoryId, parentDocumentCategoryId: x.parentDocumentCategoryId, title: x.title}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentCategory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("document-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentCategory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("document-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocumentCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`document-categories` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
            this.getDocumentCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetailCategories: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("document-detail-categories/get-all" + paramsToUrlQuery(data?.query))
          .then((response) => {
            //commit("SET_COMMON_DATA", {...state[ssid].common, documentDetailCategories: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetailCategory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("document-detail-categories/get" + paramsToUrlQuery(data?.query))
          .then((response) => {
            //commit("SET_COMMON_DATA", {...state[ssid].common, documentDetailCategories: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentDetailCategory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("document-detail-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentDetailCategory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .put("document-detail-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocumentDetailCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .delete(`document-detail-categories` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailCategories().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentTypes: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("document-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, documentTypes: response.data?.map(x => ({documentTypeId: x.documentTypeId, title: x.title, documentCategoryId: x.documentCategoryId, parentDocumentTypeId: x.parentDocumentTypeId}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("document-types/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("document-types" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("document-types" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocumentType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`document-types` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
            this.getDocumentTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetailTypes: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("document-detail-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            //commit("SET_COMMON_DATA", {...state[ssid].common, documentDetailTypes: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetailType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("document-detail-types/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentDetailType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("document-detail-types" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentDetailType: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .put("document-detail-types" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocumentDetailType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .delete(`document-detail-types` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
            this.getDocumentDetailTypes().then().catch();
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getModules: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("modules/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, modules: response.data?.map(x => ({moduleId: x.moduleId, code: x.code, title: x.title, properties: x.properties, parentModuleId: x.parentModuleId}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getModule: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("modules/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTableHistory: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("table-history/getBy" + paramsToUrlQuery(data?.query), {
            params: data?.data,
            signal: data.signal
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addModule: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("modules/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateModule: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("modules/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteModule: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("modules/delete" + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    modulesReorder: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("modules/reorder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAllDocumentTypes: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/document-types" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, documentTypes: response.data?.map(x => ({documentTypeId: x.documentTypeId, title: x.title, documentCategoryId: x.documentCategoryId, parentDocumentTypeId: x.parentDocumentTypeId}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getConfigs: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/configs" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, configs: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRightsList: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/rights" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, rightsList: response.data?.map(x => ({rightId: x.rightId, code: x.code, title: x.title}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    createMbtilesFolder: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/mbtiles/folder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error)
          });
      });
    },
    renameMbtilesFolder: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/mbtiles/rename-folder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteMbtilesFolder: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("utils/mbtiles/folder" + paramsToUrlQuery(data?.query), {
            data: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error)
          });
      });
    },
    dropMbtilesFolder: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("utils/mbtiles/drop-folder" + paramsToUrlQuery(data?.query), {
            data: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserOrganization: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserCompanies: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/companies" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCompanies: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("public/companies" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganization: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCompany: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/company" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    switchOrganization: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("users/switch-organization" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            commit("SET_USER", response.data.user);
            commit("SET_TOKEN", response.data.token);
            http.defaults.headers.common["x-access-token"] = response.data.token;
            dispatch("getRoles")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getModules", {query: {moduleTypeId: parseInt(config.APP_MODULE_TYPE)}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getRightsList")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getAllDocumentTypes")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getDocumentCategories")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getConfigs")
              .then((res) => {
              })
              .catch((err) => {
              });
            // get currentProject from IndexedDB if exists
            get("userId").then((usr) => {
              if (parseInt(usr) === response.data.user.userId) {
                get("currentProject")
                  .then((val) => {
                    dispatch("setCurrentProject", JSON.parse(val)).catch((err) => {
                    });
                  })
                  .catch((err) => {
                  });
              }
            });
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizationSettings: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/settings" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocument: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editDocument: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserProjects: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/get-projects-for-user" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getFullProject: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/get-children" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getLayerGeometries: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/get-layer-geometries" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getIsochrone: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("geometries/isochrone" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    addLayer: ({dispatch, state}, data) => {
      return new Promise(async (resolve, reject) => {
        const {RendererUtils} = await import('@/js/Map/renderer/renderer-utils');
        if (RendererUtils.isLocalLayer(data?.data)) {
          resolve(data?.data);
        } else {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
          if (data && data.headers && data.headers["x-access-token"])
            http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
          http
            .post("documents/add-layer" + paramsToUrlQuery(data?.query), data?.data)
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              throwError(error, dispatch, resolve, reject);
            });
        }
      });
    },

    mergeMbtileCollection: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/merge-mbtiles-collection" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    editLayer: ({dispatch, state}, data) => {
      return new Promise(async (resolve, reject) => {
        const {RendererUtils} = await import('@/js/Map/renderer/renderer-utils');
        if (RendererUtils.isLocalLayer(data?.data)) {
          resolve(data?.data);
        } else {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
          if (data && data.headers && data.headers["x-access-token"])
            http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
          http
            .post("documents/update-layer" + paramsToUrlQuery(data?.query), data?.data)
            .then((response) => {
              resolve(response.data);
            })
            .catch((error) => {
              throwError(error, dispatch, resolve, reject);
            });
        }
      });
    },
    getGeometryExternalDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(`geometries/external/${data.documentId}/${data.value}?geometryId=${data.geometryId}`)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getExternalDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(
            `utils/external/${data.params.documentId}/${data.params.method}` + paramsToUrlQuery(data?.query),
            data?.data
          )
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    exportToZip: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .get("documents/export-zip" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    exportLayer: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .get("documents/export" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    exportGeometries: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .post("documents/export" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getStyle: ({commit, dispatch, state}, data) => {
      // https://api.mapbox.com/styles/v1/mapbox/satellite-v9?access_token=pk.eyJ1IjoiZ2VvZGF0YS1zZXJ2aWNlcyIsImEiOiJja3J1Zzg1M3QxMXo5MnVucGdidHg5Mzl5In0.h-zRgBPYOJ8APe-8TILqQw
      data.url = data.url.replace("mapbox://styles/", "https://api.mapbox.com/styles/v1/");
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(data.url + paramsToUrlQuery({access_token: config.MAPBOX_TOKEN}))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getGeometryId: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("geometries/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addGeometry: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    mergeGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/merge" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    saveBulkGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/bulk" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    clipByGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/clip" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    splitGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/split" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    mergeRasters: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/merge-rasters" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateGeometry: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("geometries/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteGeometry: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("geometries/delete" + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    geometryAddProperty: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/add-property" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addProject: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-project" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importProject: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .post(`documents/import-project` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importLayerGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .post(`documents/import-geometries` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importLayerAttachments: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpSync.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          httpSync.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        httpSync
          .post(`documents/import-attachments` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importLayer: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        ssid = ssid ? ssid : data.ssid;
        httpSync.defaults.headers.common["x-access-token"] =
          urlParams.get("api_key") || (ssid ? state[ssid].token : undefined) || data.token;
        httpSync
          .post(`documents/import-layer` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importLayerArchive: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        ssid = ssid ? ssid : data.ssid;
        httpSync.defaults.headers.common["x-access-token"] =
          urlParams.get("api_key") || (ssid ? state[ssid].token : undefined) || data.token;
        httpSync
          .post(`documents/import-layer-archive` + paramsToUrlQuery(data?.query), data?.data.archive, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteLayer: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-layer` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocument: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteProject: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-project` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateProject: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`documents/update-project` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRoles: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("roles/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, rolesList: response.data?.map(x => ({roleId: x.roleId, title: x.title, parentRoleId: x.parentRoleId}))});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRole: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("roles/get" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRoleRights: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("roles/rights" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRights: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("rights/get-all" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addRole: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("roles/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateRole: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("roles/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteRole: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`roles/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUsers: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/active-users" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserInfo: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/get" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUser: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserRights: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/rights" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    registerUser: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;

        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];

        if (data && data.headers && data.headers["x-module-type-id"])
          http.defaults.headers.common["x-module-type-id"] = data.headers["x-module-type-id"];

        http
          .post("users/register" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    recoverPassword: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("users/recover-account" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    resetPassword: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("users/reset-password" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    upsertUser: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("users" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteUser: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`users/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    eraseUser: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`users/erase` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    searchGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/search" + paramsToUrlQuery(data?.query), data?.data.params)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            if (/401|403/.test(error?.response?.status)) {
              reject("api_key or token is missing!");
            } else {
              throwError(error, dispatch, resolve, reject);
            }
          });
      });
    },
    getIframes: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token || data.token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/iframe-list" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    addIframe: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-iframe" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateIframe: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-iframe" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteIframe: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-iframe` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIframe: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];

        if (data?.query?.apiKeyAlias) {
          http.defaults.headers.common['api-key'] = data?.query?.apiKeyAlias;
          delete http.defaults.headers.common["x-access-token"];
          delete data?.query?.apiKeyAlias;
        }
        http
          .get("documents/iframe" + paramsToUrlQuery(data?.query), {
            params: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIframeApiKey: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/iframe-generate-api-key" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIntegrations: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/integration-list" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIntegration: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/integration/" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addIntegration: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-integration" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateIntegration: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-integration" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteIntegration: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-integration` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIntegrationApiKey: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/integration-generate-api-key/" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    integrationExecute: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/integration-execute" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    documentSearch: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/search" + paramsToUrlQuery(data?.query), {...(data?.data || {}), isPrivate: data?.data?.isPrivate || false})
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    documentListByPriority: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/list-by-priority" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    externalDocumentGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token || data.token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/external/geometries" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      })
    },
    getDocumentById: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getReportById: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/report" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAttributes: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("attributes/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addAttribute: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("attributes/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateAttribute: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("attributes/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteAttribute: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("attributes/delete" + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getProjections: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/projections" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addConnection: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-connection" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateConnection: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-connection" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getConnection: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/connection" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getConnectionEntities: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/connection-entities" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getConnections: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/connection-list" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteConnection: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-connection` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    uploadLayerImage: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`uploads/layer-image` + paramsToUrlQuery(data?.query), data?.data.file, {
            params: data?.data.params || {},
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    uploadFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`uploads` + paramsToUrlQuery(data?.query), data?.data.file, {
            params: data?.data.params || {},
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    uploadSignedFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`uploads/signed-file` + paramsToUrlQuery(data?.query), data?.data.file, {
            params: data?.data.params || {},
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    identifyPlant: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`plant-identifier/identify` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    /*
     * @param url
     * @param documentId
     * */
    updateFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`uploads/update` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/filter" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getGeometryDistinctDates: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/timeline" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getIntersectedGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/filter/intersects" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    googleGeocodeSearch: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/places" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    googleGeocodePlace: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/place" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateGeometries: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/filter/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addBulkAttributes: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/bulk-attributes" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    bulkEdit: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/bulk-edit" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    downloadAttributesExcel: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/filter/export/attributes" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    /**
     *
     * @param dispatch
     * @param state
     * @param data // documentId
     * @returns {Promise<unknown>}
     */
    getProjectsAndLayers: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/get-projects-layers" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addShare: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-share" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateShare: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-share" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getShare: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/share" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getVirtualTours: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/nomenclature-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: 100,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, tours: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteVirtualTour: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .delete(`documents/delete-nomenclature` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getFlows: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: DocumentCategoryEnum.Flow,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTriggers: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: DocumentCategoryEnum.Trigger,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getFlow: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/form" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteFlow: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-form` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addFlow: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-flow" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateFlow: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSQLScripts: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: DocumentCategoryEnum.SQLScript,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSQLScript: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/form" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteSQLScript: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-form` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addSQLScript: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateSQLScript: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    executeSQLScript: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/dataset/execute" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAnalyticsTemplates: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: DocumentCategoryEnum.Analytics,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAnalyticsTemplate: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/form" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteAnalyticsTemplate: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-form` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addAnalyticsTemplate: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateAnalyticsTemplate: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSchemas: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("schemas/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSchema: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("schemas/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteSchema: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`schemas` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addSchema: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("schemas" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateSchema: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("schemas" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getNomenclatures: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/nomenclature-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: 16,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getNomenclature: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/nomenclature" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteNomenclature: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-nomenclature` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addNomenclature: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-nomenclature" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateNomenclature: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-nomenclature" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getShares: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/share-list" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSharesUser: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/shares" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocuments: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/list" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentWithDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/details" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        const query = data?.query;
        if (query)
          Object.keys(query).forEach((k) => {
            if (query[k] === null) delete query[k];
          });
        http
          .get("documents/details-list" + paramsToUrlQuery(query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getNestedDocumentDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        const query = data?.query;
        if (query)
          Object.keys(query).forEach((k) => {
            if (query[k] === null) delete query[k];
          });
        http
          .get("documents/details-list-nested" + paramsToUrlQuery(query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getNestedDocumentTasks: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        const query = data?.query;
        if (query)
          Object.keys(query).forEach((k) => {
            if (query[k] === null) delete query[k];
          });
        http
          .get("documents/tasks-list-nested" + paramsToUrlQuery(query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentDetailsOrder: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/details/reorder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentDetail: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/detail" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentDetail: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/detail" + paramsToUrlQuery(data?.query), {
            params: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDocumentDetails: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/details" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDocumentDetail: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("documents/detail" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDocumentDetail: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/detail` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    dataSourceCall: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        if (data.url && (!data.url.startsWith("http") || data.url.startsWith(config.API_URL))) {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
          if (data && data.headers && data.headers["x-access-token"])
            http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
          if (data && data.headers && data.headers["x-access-token"])
            http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        }

        let callData = {
          method: data.method || "get",
          url: data.url + paramsToUrlQuery(data?.query, !/\?/.test(data.url)),
          data: data.method !== "get" ? data.params : null,
          params: data.method === "get" ? data.params : null,
        };

        const requestType = data.requestType || "json";
        if (requestType === "formData") {
          const formData = new FormData();
          Object.keys(data.params).forEach((key) => {
            const param = data.params[key];
            if (typeof param === "object" && key !== "file") {
              formData.append(key, JSON.stringify(param));
            } else {
              formData.append(key, data.params[key]);
            }
          });
          callData.data = data.method !== "get" ? formData : null;
          // http.defaults.headers.common["content-type"] = undefined;
        }

        if (data.headers && Object.keys(data.headers).length) {
          callData.headers = {
            ...(http?.defaults?.headers?.common || {}),
            ...data.headers,
          };
        }

        if (data.body) {
          callData.data = data.body;
        }

        http(callData)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizationCategories: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organization-categories/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizationTypes: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organization-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizations: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRelatedCompanies: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/related-companies" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    publishOrganizationPortal: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/publish/portal" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteOrganizationCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`organization-categories/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteOrganizationType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`organization-types/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteOrganization: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`organizations/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addOrganizationCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organization-categories/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addOrganizationType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organization-types/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addOrganization: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organizations/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateOrganizationCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("organization-categories/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateOrganizationType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("organization-types/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateOrganization: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("organizations/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getJobs: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("jobs/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteShare: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`documents/delete-share` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getJob: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("jobs/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addJob: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("jobs/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateJob: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("jobs/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteJob: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`jobs/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEmployees: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("employees/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEmployee: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("employees/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addEmployee: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("employees/add" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateEmployee: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("employees/update" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteEmployee: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`employees/delete` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizationStructure: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/structure" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    organizationsReorder: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organizations/reorder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganizationDescendants: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("organizations/descendants" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getUserDepartments: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("users/departments" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    documentsReorder: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/reorder" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDocumentAs: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(`documents/${data.format}` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getGlobalComponents: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/form-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: !data?.data?.documentCategoryId ? 124 : data?.data?.documentCategoryId,
          })
          .then((response) => {
            commit("SET_COMMON_DATA", {...state[ssid].common, globalComponents: response.data});
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getFullGlobalComponents: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("documents/list-full" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getForms: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/form-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: !data?.data?.documentCategoryId ? 18 : data?.data?.documentCategoryId,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getForm: ({dispatch, state}, data) => {
      if (!state[ssid]?.common?.configs?.preventFormCaching && state[ssid].currentForms && !data.data?.editMode && data.data?.documentId && state[ssid].currentForms?.documentIds[data.data.documentId]) {
        return new Promise((res) => {
          res(JSON.parse(JSON.stringify(state[ssid].currentForms.documentIds[data.data.documentId])));
        });
      } else if (!state[ssid]?.common?.configs?.preventFormCaching && state[ssid].currentForms && !data.data?.editMode && data.data?.documentTypeId && state[ssid].currentForms?.documentTypeIds[data.data.documentTypeId]) {
        return new Promise((res) => {
          res(JSON.parse(JSON.stringify(state[ssid].currentForms.documentTypeIds[data.data.documentTypeId])));
        });
      } else {
        return new Promise((resolve, reject) => {
          http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
          if (data && data.headers && data.headers["x-access-token"])
            http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
          http
            .get("documents/form" + paramsToUrlQuery(data?.query), {
              params: data?.data,
            })
            .then((response) => {
              if (!data.data.editMode) {
                if (!state[ssid].currentForms) {
                  state[ssid].currentForms = {
                    documentIds: {},
                    documentTypeIds: {}
                  };
                }
                if (data.data.documentId) {
                  state[ssid].currentForms.documentIds[data.data.documentId] = response.data
                } else if (data.data.documentTypeId) {
                  state[ssid].currentForms.documentTypeIds[data.data.documentTypeId] = response.data
                }
              }
              resolve(response.data);
            })
            .catch((error) => {
              throwError(error, dispatch, resolve, reject);
            });
        });
      }
    },
    addForm: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateForm: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteForm: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-form` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getProcesses: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/form-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: !data?.data?.documentCategoryId ? DocumentCategoryEnum.Process : data?.data?.documentCategoryId,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/form" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-form" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-form` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    executeGetProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(`process-flow/${data.documentId}` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    executePostProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`process-flow/${data.documentId}` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    executePutProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put(`process-flow/${data.documentId}` + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    executeDeleteProcess: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`process-flow/${data.documentId}` + paramsToUrlQuery(data?.query), {
            data: data?.data
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTickets: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/ticket-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: 19,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getTicket: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/ticket" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addTicket: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-ticket" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addTicketItem: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-ticket-item" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateTicket: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-ticket" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteTicket: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-ticket` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getReportTemplates: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/nomenclature-list" + paramsToUrlQuery(data?.query), {
            ...data?.data,
            documentCategoryId: 49,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    htmlToPdf: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/htmlToPdf" + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    htmlToDocx: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/htmlToDocx" + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getFontsList: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get("utils/fonts/list" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getPhotoGallery: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/photo-galleries-list" + paramsToUrlQuery(data?.data), {
            ...data?.data,
            documentCategoryId: 51,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updatePhotoGallery: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/update-photo-gallery" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addPhotoGallery: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post("documents/add-photo-gallery" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    uploadImage: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .post(`uploads/image` + paramsToUrlQuery(data?.data))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteUpload: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .delete(`uploads` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getImages: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        http
          .get(`documents/uploads-list` + paramsToUrlQuery(data?.data), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAttributeCategories: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("attribute-categories/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAttributeCategory: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("attribute-categories/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addAttributeCategory: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("attribute-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateAttributeCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("attribute-categories" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteAttributeCategory: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`attribute-categories` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    rennsValidate: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("renns/validate" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    rennsExport: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("renns/export" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRennsInvalidStreets: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("renns/invalid-streets" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRennsInvalidHouseNumbers: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("renns/invalid-house-numbers" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEntities: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("entities/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEntityById: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("entities/get" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEntity: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("entities" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEntityDocuments: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("entities/documents" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteEntity: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`entities` + paramsToUrlQuery(data?.query), {data: data?.data})
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addEntity: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("entities" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateEntity: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("entities" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEntityTypes: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("entity-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getModuleTypes: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/module-types" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSettingsTypes: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/setting-types" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSettingsType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/setting-types/get" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addSettingsType: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/setting-type" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateSettingsType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/setting-type" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteSettingsType: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`utils/setting-type` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSettings: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/settings" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSetting: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/settings/get" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addSetting: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/settings" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateSetting: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/settings" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteSetting: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`utils/settings` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addOrganizationSetting: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organizations/setting" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateOrganizationSetting: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("organizations/setting" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteOrganizationSetting: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`organizations/setting` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getLayerBounds: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("geometries/extent" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getMBTileInfo: ({commit, dispatch, state}, path) => {
      return new Promise((resolve, reject) => {
        http
          .get(`${config.TILES_URL}/info/mbtiles/` + (path ? path : ""))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getMBTilesFolders: ({commit, dispatch, state}) => {
      return new Promise((resolve, reject) => {
        http
          .get(`utils/mbtiles/folders`)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getMBTilesList: ({commit, dispatch, state}, path) => {
      return new Promise((resolve, reject) => {
        http
          .get(`${config.TILES_URL}/info/mbtiles-list/` + (path ? path : ""))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    importDocument: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`documents/import-document` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    checkServer: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`utils/check`)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getAdministrativeUnits: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`administrative-units/get-all` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    testcertificate: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`utils/certificate/fill-registration` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    gethash: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .post(`tests/get-hash` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    signPDF: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http
          .post(`documents/sign-pdf` + paramsToUrlQuery(data?.query),
            data?.data,
          )
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getCertificates: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpLocal
          .get(`PdfSigner/certs` + paramsToUrlQuery(data?.query),
            data?.data,
          )
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    usbSignPDF: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        httpLocal
          .post(`PdfSigner` + paramsToUrlQuery(data?.query),
            data?.data,
          )
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    enrollUser: ({dispatch, state, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`users/certificate/enroll` + paramsToUrlQuery(data?.query), data?.data.file, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            commit("SET_USER", response.data.user);
            commit("SET_TOKEN", response.data.token);
            http.defaults.headers.common["x-access-token"] = response.data.token;
            if (config.APP_TYPE === "default") {
              dispatch("getRoles")
                .then((res) => {
                })
                .catch((err) => {
                });
            }
            dispatch("getModules", {query: {moduleTypeId: parseInt(config.APP_MODULE_TYPE)}})
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getRightsList")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getAllDocumentTypes")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getDocumentCategories")
              .then((res) => {
              })
              .catch((err) => {
              });
            dispatch("getConfigs")
              .then((res) => {
              })
              .catch((err) => {
              });
            // get currentProject from IndexedDB if exists
            get("userId").then((usr) => {
              if (parseInt(usr) === response.data.user.userId) {
                get("currentProject")
                  .then((val) => {
                    dispatch("setCurrentProject", JSON.parse(val)).catch((err) => {
                    });
                  })
                  .catch((err) => {
                  });
              }
            });
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    publishWmts: ({dispatch, commit, state}, data) => {
      return new Promise((resolve, reject) => {
        httpWmts
          .post(`${data.mbtile}/publish`, data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOperatingScheduleTypes: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`utils/operating-schedule-types`, {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getOrganzationOperatingSchedules: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`organizations/operating-schedules` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      })
    },
    getOperatingSchedulesAvailabilities: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`organizations/operating-schedule/availabilities` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      })
    },
    getScheduleTimeSlots: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .get(`organizations/operating-schedule/time-slots` + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      })
    },
    saveOrganizationSchedules: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .post("organizations/operating-schedule", data)
          .then((response) => {
            resolve(response);
          }).catch((error) => {
          if (error.response) {
            reject(error.response.data);
            // console.log(error.response.status);
            // console.log(error.response.headers);
          } else if (error.request) {
            console.log(error.request);
          } else {
            console.log('Error', error.message);
          }
          console.log(error.config);

          reject(error);
        })
      })
    },
    saveOrganizationSchedulesDetails: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .post("organizations/operating-schedule-details", data)
          .then((response) => {
            resolve(response);
          }).catch((error) => {
          if (error.response) {
            reject(error.response.data);
            // console.log(error.response.status);
            // console.log(error.response.headers);
          } else if (error.request) {
            console.log(error.request);
          } else {
            console.log('Error', error.message);
          }
          console.log(error.config);

          reject(error);
        })
      })
    },
    deleteOrganizationScheduleDetails: ({ }, data) => {
      return new Promise((resolve, reject) => {
        http
          .delete("organizations/operating-schedule-detail", data)
          .then((response) => {
            resolve(response);
          });
      })
    },
    deleteOrganizationSchedule: ({ }, id) => {
      return new Promise((resolve, reject) => {
        http
          .delete("organizations/operating-schedule", {
            data: {
              "operatingScheduleId": id
            }
          })
          .then((response) => {
            resolve(response);
          });
      })
    },
    getUserNotifications: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get(`entities/documents?documentCategoryId=141` + paramsToUrlQuery(data?.query, false), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    markNotificationsAsRead: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("entities/notifications/mark-read" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getSchemaTypes: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("schema-types/get-all" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDevices: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/device-list" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    addDevice: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/add-device" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    updateDevice: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/update-device" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteDevice: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/delete-device` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDevice: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/device/" + paramsToUrlQuery(data?.query), {
            params: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getDeviceApiKey: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/device-generate-api-key" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getRemoteDeviceMediaFiles: ({commit, dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("documents/remote/device/media-files" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    sendRemoteDeviceMediaFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/remote/device/media-file" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    deleteRemoteDeviceMediaFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete(`documents/remote/device/media-file` + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    sendRemoteDeviceRawMessage: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/remote/device/raw-message" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    sendRemoteDeviceSourceCode: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/remote/device/source-code" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    sendRemoteDeviceConfigFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/remote/device/config-file" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    sendRemoteDeviceReloadApp: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/remote/device/reload-app" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    pushRemoteDeviceSourceCode: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("documents/remote/device/source-code" + paramsToUrlQuery(data?.query), data?.data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    pushRemoteDeviceConfigFile: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .put("documents/remote/device/config-file" + paramsToUrlQuery(data?.query), data?.data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    setAttributesToLayer: ({state, dispatch, commit}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("documents/import-attributes-excel" + paramsToUrlQuery(data?.query), data?.data, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
    getEndpointsInfo: ({ state, dispatch, commit }, data) => {
      return new Promise((resolve, reject) => {
        if (state.endpointsInfo) {
          resolve(state.endpointsInfo);
          return;
        }
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("postman/endpoints" + paramsToUrlQuery(data?.query))
          .then((response) => {
            commit('SET_ENDPOINTS_INFO', response.data);
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    // MBTiles management API calls
    getMbtilesFolders: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/mbtiles/folders" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getMbtilesTree: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/mbtiles/tree" + paramsToUrlQuery(data?.query))
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getMbtilesFiles: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .get("utils/mbtiles/files/" + data?.query?.path)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    renameMbtilesFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/mbtiles/rename-file" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    deleteMbtilesFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .delete("utils/mbtiles/file" + paramsToUrlQuery(data?.query), {
            data: data?.data,
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    convertTifToMbtiles: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/mbtiles/convert" + paramsToUrlQuery(data?.query), data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    uploadMbtilesFile: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post("utils/mbtiles/upload-file/" + data?.data?.path, data?.data.file, {
            params: data?.data?.params || {},
            headers: {
              "Content-Type": "multipart/form-data",
            }
          })
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getGalleryFiles: ({ dispatch, state }, data) => {
      return new Promise((resolve, reject) => {
        axios.get('http://localhost:5000/Folder/Files' + paramsToUrlQuery(data), {
          headers: {
            'x-access-token': state[ssid].token
          }
        }).then(res => {
          resolve(res.data);
        }).catch(err => {
          throwError(err, dispatch, resolve, reject);
        });
      });
    },

    postGalleryFiles: ({ dispatch, state }, data) => {
      return new Promise((resolve, reject) => {
        axios.post('http://localhost:5000/Folder/UploadFiles', data, {
          headers: {
            'x-access-token': state[ssid].token
          }
        }).then(res => {
          resolve(res.data);
        }).catch(err => {
          throwError(err, dispatch, resolve, reject);
        })
      });
    },

    restoreData: ({dispatch, state}, data) => {
      return new Promise((resolve, reject) => {
        http.defaults.headers.common["x-access-token"] = urlParams.get("api_key") || state[ssid].token;
        if (data && data.headers && data.headers["x-access-token"])
          http.defaults.headers.common["x-access-token"] = data.headers["x-access-token"];
        http
          .post(`utils/restore/${data?.tableName}/${data?.tableId}`, data?.data)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },
  },
  plugins: [localforagePlugin.plugin, localstoragePlugin.plugin],
});
export default store;
